import { db, makeId } from "./db";
import type { ExerciseDefinition, ExercisePerformance, SetPerformance, WorkoutSession, WorkoutTemplate } from "./types";

export type SetChanges = Partial<Pick<SetPerformance, "load" | "reps" | "duration" | "rir" | "completedAt">>;

export function updatePerformanceSet(performance: ExercisePerformance, setIndex: number, changes: SetChanges) {
  if (!performance.sets[setIndex]) throw new Error("Série introuvable.");
  const followingSettings: SetChanges = {
    ...(changes.load !== undefined ? { load: changes.load } : {}),
    ...(Object.hasOwn(changes, "reps") ? { reps: changes.reps } : {}),
    ...(Object.hasOwn(changes, "duration") ? { duration: changes.duration } : {}),
    ...(Object.hasOwn(changes, "rir") ? { rir: changes.rir } : {}),
  };
  return {
    ...performance,
    sets: performance.sets.map((set, index) => {
      if (index === setIndex) return { ...set, ...changes };
      if (setIndex === 0 && index > 0 && !set.completedAt) return { ...set, ...followingSettings };
      return set;
    }),
  };
}

export async function createSessionSnapshot(template: WorkoutTemplate): Promise<WorkoutSession> {
  const definitions = await db.exercises.bulkGet(
    template.exercises.map((item) => item.exerciseDefinitionId),
  );
  const performances: ExercisePerformance[] = template.exercises.flatMap((item, index) => {
    const definition = definitions[index] as ExerciseDefinition | undefined;
    if (!definition || definition.archived) return [];
    return [{
      id: makeId("performance"),
      exerciseDefinitionId: definition.id,
      exerciseNameSnapshot: definition.name,
      positionSnapshot: item.position,
      prescriptionSnapshot: {
        minSetCount: item.minSetCount,
        setCount: item.setCount,
        minReps: item.minReps,
        maxReps: item.maxReps,
        minDuration: item.minDuration,
        maxDuration: item.maxDuration,
        targetRirMin: item.targetRirMin,
        targetRirMax: item.targetRirMax,
        restSeconds: item.restSeconds,
        load: item.load,
        unit: item.unit,
        loadIncrement: item.loadIncrement,
        trackingMode: definition.trackingMode,
        isUnilateral: definition.isUnilateral,
        perSide: definition.perSide,
        progressionMode: definition.progressionMode,
        discreteSteps: item.discreteSteps,
        discreteStepIndex: item.discreteStepIndex,
      },
      sets: Array.from({ length: item.setCount }, (_, setIndex) => ({
        id: makeId("set"),
        setNumber: setIndex + 1,
        reps: definition.trackingMode === "reps" ? item.minReps : undefined,
        duration: definition.trackingMode === "duration" ? item.minDuration : undefined,
        load: item.load,
        unit: item.unit,
        rir: item.targetRirMin ?? 1,
      })),
    }];
  });

  return {
    id: makeId("session"),
    workoutTemplateId: template.id,
    templateNameSnapshot: template.name,
    startedAt: Date.now(),
    status: "active",
    currentExerciseIndex: 0,
    performances,
  };
}

export const completedSetCount = (session: WorkoutSession) =>
  session.performances.reduce((total, performance) => total + performance.sets.filter((set) => set.completedAt).length, 0);

export async function abandonWorkoutSession(session: WorkoutSession, completedAt = Date.now()) {
  if (session.status !== "active") throw new Error("Seule une séance en cours peut être abandonnée.");
  if (completedSetCount(session) === 0) {
    await db.sessions.delete(session.id);
    return undefined;
  }
  const abandoned: WorkoutSession = { ...session, status: "abandoned", completedAt, restTimer: undefined };
  await db.sessions.put(abandoned);
  return abandoned;
}

export async function lastPerformance(exerciseDefinitionId: string, before = Date.now()) {
  const sessions = await db.sessions.where("status").equals("completed").sortBy("completedAt");
  for (const session of sessions.reverse()) {
    if ((session.completedAt ?? 0) >= before) continue;
    const performance = session.performances.find((item) => item.exerciseDefinitionId === exerciseDefinitionId);
    if (performance?.sets.some((set) => set.completedAt)) return performance;
  }
}

export function recordedExerciseStats(sessions: WorkoutSession[]) {
  const stats = new Map<string, { sessionIds: Set<string>; maxByUnit: Map<string, number> }>();
  for (const session of sessions) {
    if (session.status === "active") continue;
    for (const performance of session.performances) {
      const recorded = performance.sets.filter((set) => set.completedAt);
      if (!recorded.length) continue;
      const entry = stats.get(performance.exerciseDefinitionId) ?? { sessionIds: new Set<string>(), maxByUnit: new Map<string, number>() };
      entry.sessionIds.add(session.id);
      for (const set of recorded) entry.maxByUnit.set(set.unit, Math.max(entry.maxByUnit.get(set.unit) ?? 0, set.load));
      stats.set(performance.exerciseDefinitionId, entry);
    }
  }
  return stats;
}
